// ============================================================================
// BRANCH MASTER SCREEN — MSTR-BRN-001
// ============================================================================
// Fourth and final level of the hierarchy. Introduces:
//   - 3-level cascading FK filter (Zone → Region → Area)
//   - Area dropdown labels carry full hierarchy context (Area · Region · Zone)
//   - 3-level ancestor cascade filter on the form Area dropdown: only areas
//     whose region AND zone are ACTIVE + not deleted appear (UI side of the
//     defense-in-depth; the backend AreaExistsAsync enforces the same gate).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useBranches } from 'hooks/api/useBranches';
import { useAreas } from 'hooks/api/useAreas';
import { useRegions } from 'hooks/api/useRegions';
import { useZones } from 'hooks/api/useZones';
import { branchService } from 'api/services/branchService';
import type { Branch, BranchSearchRequest } from 'api/types/branch.types';
import BranchFormModal, { type AreaOption } from 'components/modals/modules/branch-module/BranchFormModal';

type ViewMode = 'list' | 'grid';

interface BranchMasterScreenProps {
  /** Passed by main_layout when rendered as a tabbed module. */
  tabId?: string;
  moduleName?: string;
}

const BranchMasterScreen: React.FC<BranchMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // ----- Server-side query params -----
  const [queryParams, setQueryParams] = useState<BranchSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useBranches(queryParams);

  // ----- Parent lookups -----
  // Form modal: active areas. We still have to cascade-filter by ancestor state
  // client-side (see activeRegionIds / activeZoneIds below).
  const { data: areas, refetch: refetchAreas } = useAreas({ page: 1, pageSize: 500 });
  // Filter dropdown: all areas (regardless of state).
  const { data: allAreas, refetch: refetchAllAreas } = useAreas({
    page: 1, pageSize: 500, statusCode: 'ALL',
  });
  // Filter dropdown: all regions (grandparent cascade) + ancestor-state resolver.
  const { data: allRegions, refetch: refetchAllRegions } = useRegions({
    page: 1, pageSize: 200, statusCode: 'ALL',
  });
  // Filter dropdown: all zones (great-grandparent cascade) + ancestor-state resolver.
  const { data: allZones, refetch: refetchAllZones } = useZones({
    page: 1, pageSize: 100, statusCode: 'ALL',
  });

  // Ancestor-active sets for cascade filtering on the form Area dropdown.
  // An area is eligible ONLY when its own state, its parent region's state, and
  // its grand-parent zone's state are all ACTIVE + not deleted.
  const activeZoneIds = useMemo(() => {
    const set = new Set<string>();
    (allZones ?? []).forEach(z => {
      if (!z.deletedAt && z.statusCode === 'ACTIVE') set.add(z.id);
    });
    return set;
  }, [allZones]);

  const activeRegionIds = useMemo(() => {
    const set = new Set<string>();
    (allRegions ?? []).forEach(r => {
      if (!r.deletedAt && r.statusCode === 'ACTIVE' && activeZoneIds.has(r.zoneId)) {
        set.add(r.id);
      }
    });
    return set;
  }, [allRegions, activeZoneIds]);

  // Active-area options shown in the form modal. Label shows full hierarchy.
  // Guard: when ancestor lookups haven't loaded yet, show all areas (no filter)
  // to avoid hiding the dropdown's entire contents during initial render.
  const areaOptions: AreaOption[] = useMemo(() => {
    const source = (areas ?? []);
    const ancestorsLoaded = activeRegionIds.size > 0 && activeZoneIds.size > 0;
    const filtered = ancestorsLoaded
      ? source.filter(a => a.regionId && activeRegionIds.has(a.regionId))
      : source;
    return filtered.map(a => {
      const region = a.regionName ? ` · ${a.regionName}` : '';
      const zone = a.zoneName ? ` · ${a.zoneName}` : '';
      return {
        value: a.id,
        label: `${a.areaCode} — ${a.areaName}${region}${zone}`,
      };
    });
  }, [areas, activeRegionIds, activeZoneIds]);

  // ----- Filter panel state -----
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [areaFilter, setAreaFilter] = useState<string>('');
  const [branchCodeFilter, setBranchCodeFilter] = useState('');
  const [branchNameFilter, setBranchNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Filter-panel Zone dropdown — all zones, status-suffixed.
  const zoneFilterOptions = useMemo(() => {
    const list = (allZones ?? []).map(z => {
      let suffix = '';
      if (z.deletedAt) suffix = ' (Deleted)';
      else if (z.statusCode && z.statusCode !== 'ACTIVE') suffix = ` (${z.statusCode})`;
      return { value: z.id, label: `${z.zoneCode} — ${z.zoneName}${suffix}` };
    });
    return [{ value: '', label: 'All zones' }, ...list];
  }, [allZones]);

  // Filter-panel Region dropdown — cascades off the selected zone filter.
  const regionFilterOptions = useMemo(() => {
    const source = (allRegions ?? []).filter(r => !zoneFilter || r.zoneId === zoneFilter);
    const list = source.map(r => {
      let suffix = '';
      if (r.deletedAt) suffix = ' (Deleted)';
      else if (r.statusCode && r.statusCode !== 'ACTIVE') suffix = ` (${r.statusCode})`;
      const zoneSuffix = !zoneFilter && r.zoneCode ? ` · ${r.zoneCode}` : '';
      return {
        value: r.id,
        label: `${r.regionCode} — ${r.regionName}${zoneSuffix}${suffix}`,
      };
    });
    return [{ value: '', label: 'All regions' }, ...list];
  }, [allRegions, zoneFilter]);

  // Filter-panel Area dropdown — cascades off zone/region filters.
  const areaFilterOptions = useMemo(() => {
    const source = (allAreas ?? []).filter(a => {
      if (regionFilter) return a.regionId === regionFilter;
      if (zoneFilter) return a.zoneId === zoneFilter;
      return true;
    });
    const list = source.map(a => {
      let suffix = '';
      if (a.deletedAt) suffix = ' (Deleted)';
      else if (a.statusCode && a.statusCode !== 'ACTIVE') suffix = ` (${a.statusCode})`;
      const parentSuffix = !regionFilter && a.regionCode ? ` · ${a.regionCode}` : '';
      return {
        value: a.id,
        label: `${a.areaCode} — ${a.areaName}${parentSuffix}${suffix}`,
      };
    });
    return [{ value: '', label: 'All areas' }, ...list];
  }, [allAreas, zoneFilter, regionFilter]);

  // Invalidate stale cascaded filters when their ancestor changes.
  useEffect(() => {
    if (!zoneFilter || !regionFilter) return;
    const r = (allRegions ?? []).find(rx => rx.id === regionFilter);
    if (r && r.zoneId !== zoneFilter) setRegionFilter('');
  }, [zoneFilter, regionFilter, allRegions]);
  useEffect(() => {
    if (!areaFilter) return;
    const a = (allAreas ?? []).find(ax => ax.id === areaFilter);
    if (!a) return;
    if (regionFilter && a.regionId !== regionFilter) setAreaFilter('');
    else if (!regionFilter && zoneFilter && a.zoneId !== zoneFilter) setAreaFilter('');
  }, [areaFilter, regionFilter, zoneFilter, allAreas]);

  // ----- View state -----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Debounced global search → server
  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ----- Form modal state -----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  // Seed area options — surface the parent area of the branch under edit when
  // it (or one of its ancestors) is no longer in the active list. The branch
  // row already carries denormalised area/region/zone codes + names from the
  // 4-level JOIN, so we have the display text locally. Suffix derives from the
  // accurate state in `allAreas`/`allRegions`/`allZones`.
  const seedAreaOptions: AreaOption[] = useMemo(() => {
    if (formMode !== 'edit' || !selectedBranch) return [];
    const b = selectedBranch;
    if (areaOptions.some(o => o.value === b.areaId)) return [];

    const parentArea = (allAreas ?? []).find(a => a.id === b.areaId);
    // Derive the reason this area isn't selectable — look at the most-specific
    // inactive/deleted ancestor to give the user accurate context.
    let suffix = ' (Inactive)';
    if (parentArea) {
      if (parentArea.deletedAt) suffix = ' (Deleted)';
      else if (parentArea.statusCode && parentArea.statusCode !== 'ACTIVE') {
        suffix = ` (${parentArea.statusCode})`;
      } else if (parentArea.regionId) {
        const parentRegion = (allRegions ?? []).find(r => r.id === parentArea.regionId);
        if (parentRegion) {
          if (parentRegion.deletedAt) suffix = ' (Region Deleted)';
          else if (parentRegion.statusCode && parentRegion.statusCode !== 'ACTIVE') {
            suffix = ` (Region ${parentRegion.statusCode})`;
          } else {
            const parentZone = (allZones ?? []).find(z => z.id === parentRegion.zoneId);
            if (parentZone?.deletedAt) suffix = ' (Zone Deleted)';
            else if (parentZone?.statusCode && parentZone.statusCode !== 'ACTIVE') {
              suffix = ` (Zone ${parentZone.statusCode})`;
            } else {
              suffix = '';
            }
          }
        }
      }
    }

    const region = b.regionName ? ` · ${b.regionName}` : '';
    const zone = b.zoneName ? ` · ${b.zoneName}` : '';
    return [{
      value: b.areaId,
      label: `${b.areaCode ?? '?'} — ${b.areaName ?? 'Unknown'}${region}${zone}${suffix}`,
    }];
  }, [formMode, selectedBranch, areaOptions, allAreas, allRegions, allZones]);

  // Minimize support
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      // Refetch all ancestor lookups — any cross-tab change above must reflect.
      refetchAreas();
      refetchAllAreas();
      refetchAllRegions();
      refetchAllZones();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(b => b.branchCode === recordCode);
        if (rec) setSelectedBranch(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data, refetchAreas, refetchAllAreas, refetchAllRegions, refetchAllZones]
  );

  // ----- Delete state -----
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);

  // ----- Handlers -----
  const handleAddNew = useCallback(() => {
    refetchAreas();
    refetchAllAreas();
    refetchAllRegions();
    refetchAllZones();
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedBranch(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen, refetchAreas, refetchAllAreas, refetchAllRegions, refetchAllZones]);

  const handleEdit = useCallback(
    (branch: Branch) => {
      refetchAreas();
      refetchAllAreas();
      refetchAllRegions();
      refetchAllZones();
      const modalId = tabId ? `${tabId}-form-modal-edit-${branch.branchCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedBranch(branch);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal, refetchAreas, refetchAllAreas, refetchAllRegions, refetchAllZones]
  );

  const handleView = useCallback(
    (branch: Branch) => {
      const modalId = tabId ? `${tabId}-view-modal-${branch.branchCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedBranch(branch);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (branch: Branch) => {
      setSelectedBranch(branch);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((branch: Branch) => {
    setBranchToDelete(branch);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!branchToDelete) return;
    setIsDeleting(true);
    try {
      await branchService.delete(branchToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Branch "${branchToDelete.branchCode}" deleted successfully.`);
      setBranchToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [branchToDelete, refetch]);

  const handleSaved = useCallback(() => {
    refetch();
    refetchAreas();
    refetchAllAreas();
    refetchAllRegions();
    refetchAllZones();
  }, [refetch, refetchAreas, refetchAllAreas, refetchAllRegions, refetchAllZones]);

  const handleRefresh = useCallback(() => {
    refetch();
    refetchAreas();
    refetchAllAreas();
    refetchAllRegions();
    refetchAllZones();
  }, [refetch, refetchAreas, refetchAllAreas, refetchAllRegions, refetchAllZones]);

  // Pagination (0-based → 1-based)
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Filter Apply / Clear
  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      zoneId: zoneFilter || undefined,
      regionId: regionFilter || undefined,
      areaId: areaFilter || undefined,
      branchCode: branchCodeFilter.trim() || undefined,
      branchName: branchNameFilter.trim() || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [zoneFilter, regionFilter, areaFilter, branchCodeFilter, branchNameFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setZoneFilter('');
    setRegionFilter('');
    setAreaFilter('');
    setBranchCodeFilter('');
    setBranchNameFilter('');
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.zoneId) count++;
    if (queryParams.regionId) count++;
    if (queryParams.areaId) count++;
    if (queryParams.branchCode) count++;
    if (queryParams.branchName) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.zoneId, queryParams.regionId, queryParams.areaId, queryParams.branchCode, queryParams.branchName, queryParams.statusCode]);

  // ----- Grid columns -----
  const columns: SyutGridColumn<Branch>[] = useMemo(() => [
    {
      id: 'zoneName',
      header: 'Zone',
      accessorKey: 'zoneName',
      size: 120,
      enableSorting: true,
      cell: ({ row }) => row.original.zoneName ?? row.original.zoneCode ?? '-',
    },
    {
      id: 'regionName',
      header: 'Region',
      accessorKey: 'regionName',
      size: 160,
      enableSorting: true,
      cell: ({ row }) => row.original.regionName ?? row.original.regionCode ?? '-',
    },
    {
      id: 'areaName',
      header: 'Area',
      accessorKey: 'areaName',
      size: 160,
      enableSorting: true,
      cell: ({ row }) => row.original.areaName ?? row.original.areaCode ?? '-',
    },
    {
      id: 'branchCode',
      header: 'Branch Code',
      accessorKey: 'branchCode',
      size: 140,
      enableSorting: true,
    },
    {
      id: 'branchName',
      header: 'Branch Name',
      accessorKey: 'branchName',
      size: 240,
      enableSorting: true,
      flex: true,
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const b = row.original;
        const code = b.deletedAt ? 'DELETED' : (b.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={b.deletedAt ? `Deleted at ${new Date(b.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try {
          return new Date(v).toLocaleString();
        } catch {
          return v;
        }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  // ----- Row actions -----
  const getRowActions = useCallback((branch: Branch): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(branch) },
    { id: 'edit',
      label: 'Edit',
      icon: <IconSystem.edit size={16} />,
      onClick: () => handleEdit(branch),
    },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(branch) },
    { id: 'delete',
      label: 'Delete',
      icon: <IconSystem.trash size={16} />,
      onClick: () => handleDeleteClick(branch),
      variant: 'danger',
    },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  // ----- Header action buttons -----
  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add New Branch',
      onClick: handleAddNew,
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Branch Master"
        description="Leaf of the branch reporting hierarchy. Each branch rolls up into an area."
        icon={<IconSystem.home size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, area, region, zone...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No branches found"
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((b) => (
                    <div
                      key={String(b.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(b)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(b)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {b.zoneName ?? b.zoneCode} · {b.regionName ?? b.regionCode} · {b.areaName ?? b.areaCode} · {b.branchCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {b.branchName}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: b.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {b.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Zone"
                options={zoneFilterOptions}
                value={zoneFilter}
                onChange={(v) => setZoneFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Region"
                options={regionFilterOptions}
                value={regionFilter}
                onChange={(v) => setRegionFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Area"
                options={areaFilterOptions}
                value={areaFilter}
                onChange={(v) => setAreaFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Branch Code
              </label>
              <input
                type="text"
                className="form-input"
                value={branchCodeFilter}
                onChange={(e) => setBranchCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                Branch Name
              </label>
              <input
                type="text"
                className="form-input"
                value={branchNameFilter}
                onChange={(e) => setBranchNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          branchToDelete
            ? `Are you sure you want to delete branch "${branchToDelete.branchCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setBranchToDelete(null);
          }
        }}
      />

      <BranchFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedBranch}
        areaOptions={areaOptions}
        seedAreaOptions={seedAreaOptions}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default BranchMasterScreen;
